import type { TelemetryPointResponse } from 'generatedTypes/flight/models/telemetryPointResponse';
import type { Feature, MultiPolygon, Polygon, Position } from 'geojson';

import { booleanPointInPolygon, point as turfPoint } from '@turf/turf';

import { calculateAppliedRateGalPerAc } from './applicationRateUtil';

export type RateSegment = {
    end: Position;
    rateGalPerAc: number;
    start: Position;
    swathMeters: number;
};

type BuildRateSegmentsArgs = {
    fallbackSwathMeters: number;
    points: TelemetryPointResponse[];
};

/**
 * Walks consecutive telemetry points and emits one RateSegment per pair where
 * both endpoints were spraying. Each segment's rate is computed from the
 * starting point's flow × ground-speed × swath snapshot.
 */
export function buildRateSegments({
    fallbackSwathMeters,
    points
}: BuildRateSegmentsArgs): RateSegment[] {
    if (points.length < 2) {
        return [];
    }

    const segments: RateSegment[] = [];

    for (let i = 0; i < points.length - 1; i++) {
        const startPoint = points[i];
        const endPoint = points[i + 1];

        if (!startPoint.isPumpOn || !endPoint.isPumpOn) {
            continue;
        }

        if (
            startPoint.longitude === endPoint.longitude &&
            startPoint.latitude === endPoint.latitude
        ) {
            continue;
        }

        const swathMeters =
            startPoint.swathWidthM > 0 ? startPoint.swathWidthM : fallbackSwathMeters;
        const speedMetersPerSecond = Math.sqrt(
            startPoint.velocityX ** 2 + startPoint.velocityY ** 2
        );
        const rateGalPerAc = calculateAppliedRateGalPerAc({
            flowRateLitersPerMinute: startPoint.flowRateLitersPerMinute,
            speedMetersPerSecond,
            swathWidthMeters: swathMeters
        });

        if (!Number.isFinite(rateGalPerAc)) {
            continue;
        }

        segments.push({
            end: [endPoint.longitude, endPoint.latitude],
            rateGalPerAc,
            start: [startPoint.longitude, startPoint.latitude],
            swathMeters
        });
    }

    return segments;
}

type ComputePassRateArgs = {
    passPolygon: Feature<Polygon | MultiPolygon>;
    segments: RateSegment[];
};

/**
 * Returns a length-weighted mean rate for a pass by keeping only rate segments
 * whose midpoint falls inside the pass's swath polygon. Returns null when no
 * segment matches (e.g., sim paths with no telemetry, or passes fully outside
 * the recorded telemetry window).
 */
export function computePassRateGalPerAc({
    passPolygon,
    segments
}: ComputePassRateArgs): number | null {
    let weightedRateSum = 0;
    let totalLength = 0;

    for (const segment of segments) {
        const midpoint: Position = [
            (segment.start[0] + segment.end[0]) / 2,
            (segment.start[1] + segment.end[1]) / 2
        ];

        if (!booleanPointInPolygon(turfPoint(midpoint), passPolygon)) {
            continue;
        }

        const dx = segment.end[0] - segment.start[0];
        const dy = segment.end[1] - segment.start[1];
        const length = Math.sqrt(dx * dx + dy * dy);

        weightedRateSum += segment.rateGalPerAc * length;
        totalLength += length;
    }

    if (totalLength === 0) {
        return null;
    }

    return weightedRateSum / totalLength;
}
